import { createFileRoute, useRouter, useRouteContext } from "@tanstack/react-router";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { CpsInput } from "corpus";
import Left from "@repo/icons/default/Left";
import { useQueryClient } from "@tanstack/react-query";
import { graphql } from "@repo/graphql-types";
import { useSetAtom } from "jotai";
import { confirmDocumentAtom } from "@/lib/atoms/confirm-document-atom";
import {
  HeaderButton,
  HeaderRoot,
  HeaderTitle,
} from "@/components/ui/layout/header/header";
import { Page } from "@/components/ui/layout/page/page";
import {
  FormRoot,
  FormHandlerSubmit,
  FormField,
  FormSubmitButton,
  FormControl,
  FormItem,
} from "@/components/ui/form/form";
import { formatStringToNumber } from "@/lib/helpers/string";
import { useGraphQLMutation, queryFn } from "@/hooks/use-graphql";

import type { JSX } from "react";

const ConsultaCnpjQuery = graphql(/* GraphQL */ `
  query ConsultaCnpjQuery($cnpj: String!) {
    consultaCnpj(cnpj: $cnpj) {
      cnpj
      nomeEmpresa
    }
  }
`);

const CheckCnpjExistsQuery = graphql(/* GraphQL */ `
  query CheckCnpjExistsQuery($cnpj: String!) {
    tbClinicas(where: { cnpj: { _eq: $cnpj } }) {
      codClinica
      cnpj
    }
  }
`);

const formSchema = z.object({
  cnpj: z
    .string({
      error: (issue) => (issue.input === undefined ? "Campo obrigatório" : undefined),
    })
    .length(14, "O CNPJ deve ter 14 dígitos")
    .regex(/^\d+$/, "Digite apenas números"),
});

type FormFields = z.infer<typeof formSchema>;

const MAX_RETRIES = 5;
const RETRY_DELAY = 1000;

const CheckCnpjPage = (): JSX.Element => {
  const router = useRouter();
  const queryClient = useQueryClient();
  const { authClient } = useRouteContext({ from: "__root__" });
  const setConfirmDocumentAtom = useSetAtom(confirmDocumentAtom);

  const form = useForm<FormFields>({
    resolver: zodResolver(formSchema),
  });

  const { mutateAsync: consultaCnpjMutateAsync, isPending: isConsultingCnpj } =
    useGraphQLMutation(ConsultaCnpjQuery, {
      retry: MAX_RETRIES,
      retryDelay: RETRY_DELAY,
    });

  const handleSubmit = async (data: FormFields): Promise<void> => {
    try {
      const consultaResult = await consultaCnpjMutateAsync({ cnpj: data.cnpj });

      if (!consultaResult.consultaCnpj) {
        form.setError("cnpj", {
          type: "validation",
          message: "CNPJ não encontrado. Verifique o número digitado.",
        });
        return;
      }

      const checkResult = await queryClient.fetchQuery<{
        tbClinicas: { codClinica: number; cnpj: string }[];
      }>({
        queryKey: ["CheckCnpjExists", data.cnpj],
        queryFn: () => queryFn(CheckCnpjExistsQuery, authClient, { cnpj: data.cnpj }),
      });

      if (checkResult.tbClinicas.length > 0) {
        form.setError("cnpj", {
          type: "validation",
          message: "CNPJ já cadastrado no sistema.",
        });
        return;
      }

      setConfirmDocumentAtom({
        cnpj: consultaResult.consultaCnpj.cnpj ?? undefined,
        nomeEmpresa: consultaResult.consultaCnpj.nomeEmpresa ?? undefined,
        source: "external",
      });

      void router.navigate({
        to: "/settings/payments/create-recipient/confirm-document",
      });
    } catch (_error) {
      form.setError("cnpj", {
        type: "validation",
        message: "Erro ao validar CNPJ. Tente novamente.",
      });
    }
  };

  const formatCnpj = (value: string): string => {
    const numbers = formatStringToNumber(value);
    if (numbers.length <= 2) return numbers;
    if (numbers.length <= 5) return `${numbers.slice(0, 2)}.${numbers.slice(2)}`;
    if (numbers.length <= 8)
      return `${numbers.slice(0, 2)}.${numbers.slice(2, 5)}.${numbers.slice(5)}`;
    if (numbers.length <= 12)
      return `${numbers.slice(0, 2)}.${numbers.slice(2, 5)}.${numbers.slice(5, 8)}/${numbers.slice(8)}`;
    return `${numbers.slice(0, 2)}.${numbers.slice(2, 5)}.${numbers.slice(5, 8)}/${numbers.slice(8, 12)}-${numbers.slice(12, 14)}`;
  };

  return (
    <>
      <HeaderRoot>
        <HeaderButton icon={Left} align="start" />
        <HeaderTitle title="Cadastrar conta" align="center" />
      </HeaderRoot>
      <Page>
        <FormRoot {...form}>
          <FormHandlerSubmit handleSubmit={handleSubmit}>
            <div className="flex flex-col gap-6">
              <h1 className="text-xl font-semibold text-neutral-600">
                Qual é o CNPJ de titularidade da conta?
              </h1>

              <FormField
                control={form.control}
                name="cnpj"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <CpsInput
                        title=""
                        type="text"
                        inputMode="numeric"
                        maxLength={18}
                        placeholder="00.000.000/0000-00"
                        {...field}
                        value={formatCnpj(field.value || "")}
                        onChange={(e) => {
                          const numbers = formatStringToNumber(e.target.value);
                          field.onChange(numbers);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormSubmitButton disabled={isConsultingCnpj}>
                {isConsultingCnpj ? "Validando CNPJ..." : "Continuar"}
              </FormSubmitButton>
            </div>
          </FormHandlerSubmit>
        </FormRoot>
      </Page>
    </>
  );
};

export const Route = createFileRoute("/settings/payments/create-recipient/check-cnpj")({
  component: CheckCnpjPage,
});
